// saveOrderPos payload builder (cash v1) — matches the field allowlist Process_order.php reads.
// Form-encoded values are all Strings; item_ses is a JSON string. Per spec §9 + verified server contract:
//  - per-line identity is tag_id (handled in itemSession)
//  - top-level `discount` rolls coupon + custom + bogo + mix&match
//  - tip is added to amt server-side → we send tip "" and do NOT pre-add it
//  - client order_id accepted verbatim (uniqueness only)

import { roundHalfUp, toNum, truncate2 } from "./money";
import { dualTenderKind, applyDualItemSession, dualOrderFields, applyDualSplitBlend, splitCashBaseFraction } from "./dualPayload";
import { isDualPrice, normalizeSurchargeMode, surchargeMultiplier, toBasePrice } from "./dualPrice";
import { buildItemSession } from "./itemSession";
import { tenderFlag, buildGiftcardJson, buildSplitpayJson } from "./splitTender";
import { lotteryPayoutJson, lotteryPayoutTotals, lotteryScratcherStatus } from "./lotteryPayout";

const S = (v) => (v == null ? "" : String(v));
const money = (v) => roundHalfUp(v).toFixed(2);

function catalogOf(line) {
  const m = line.model || {};
  if (line.catalogUnitPrice != null) return toNum(line.catalogUnitPrice);
  return toNum(line.isVariant ? m.vPrice : m.price);
}

// 16-char uppercase hex. Server does no format validation (uniqueness dup-check only), so this
// satisfies the contract; it intentionally diverges from Android's SHA-256 (which is also arbitrary hex).
export function generateOrderId() {
  let hex = "";
  const g = typeof window !== "undefined" ? window : {};
  if (g.crypto && g.crypto.getRandomValues) {
    const buf = new Uint8Array(8);
    g.crypto.getRandomValues(buf);
    hex = Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
  } else {
    for (let i = 0; i < 16; i++) hex += Math.floor(Math.random() * 16).toString(16);
  }
  return hex.toUpperCase().slice(0, 16);
}

// RFC-4122 UUID v4. Payroc requires the terminal Idempotency-Key to be a UUID v4 (Android sends
// UUID.randomUUID()); a non-UUID key 400s. Prefer crypto.randomUUID, fall back to a manual v4.
export function generateUuidV4() {
  const g = typeof window !== "undefined" ? window : {};
  if (g.crypto && typeof g.crypto.randomUUID === "function") return g.crypto.randomUUID();
  const b = new Uint8Array(16);
  if (g.crypto && g.crypto.getRandomValues) g.crypto.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40; // version 4
  b[8] = (b[8] & 0x3f) | 0x80; // variant 10
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0"));
  return `${h[0]}${h[1]}${h[2]}${h[3]}-${h[4]}${h[5]}-${h[6]}${h[7]}-${h[8]}${h[9]}-${h[10]}${h[11]}${h[12]}${h[13]}${h[14]}${h[15]}`;
}

const pad2 = (n) => String(n).padStart(2, "0");
// LOCAL "yyyy-MM-dd HH:mm:ss" — matches Android getCurrentDateTimeWithTimeStamp(). The server does
// date('Y-m-d H:i:s', strtotime(order_date_time)); an ISO string with 'T'/'Z'/millis (or "") makes
// strtotime() return false → date(0) → "12/31/1969 04:00 PM". Send bare local like Android instead.
// Loyalty points EARNED on this order — byte-faithful port of Android getAmountForEarnPointOnly +
// getItemPriceWithDiscountTax (OCV ~2875 / ~3314). Over the earn-eligible lines (category earn_point
// on), each line's gross unit price is reduced by the ORDER-level coupon %, gift-card %, and — for
// use-point lines — loyalty %, then its per-tax amount is added and its gift-card share subtracted;
// the sum is capped at the grand total, times the earn rate (points_per_dollar + active bonus).
// Returns { points, amount } (amount = points × redemption_value). Pure & unit-tested.
export function computeLoyaltyEarn({ cart = [], rate = 0, dollarPerPoint = 0, couponDiscountValue = 0, giftCardTotal = 0, loyaltyAmt = 0, grandTotal = 0, convertBase = null } = {}) {
  if (!(rate > 0)) return { points: 0, amount: 0 };
  const grossUnit = (l) => (toNum(l.orderUnitNewPrice) > 0 ? toNum(l.orderUnitNewPrice) : toNum(l.orderUnitPrice));
  const isPayout = (l) => l.isLotteryPayout || l.isScratcherPayout;
  const mSubTotal = cart.reduce((s, l) => (isPayout(l) ? s : s + grossUnit(l) * toNum(l.orderCount)), 0);
  const usePointSub = cart.reduce((s, l) => (l.canUsePoint && !isPayout(l) ? s + grossUnit(l) * toNum(l.orderCount) : s), 0);
  const couponPct = mSubTotal > 0 ? toNum(couponDiscountValue) / mSubTotal : 0;
  const giftCardPct = mSubTotal > 0 ? toNum(giftCardTotal) / mSubTotal : 0;
  const loyaltyPct = usePointSub > 0 ? toNum(loyaltyAmt) / usePointSub : 0;
  const itemEarnBase = (l, loyPct) => {
    const unit = grossUnit(l);
    const count = toNum(l.orderCount);
    let itemDisc = 0;
    if (toNum(l.orderDiscount) > 0) itemDisc = toNum(l.orderDiscount);
    else if (toNum(l.orderDiscountPercent) > 0) itemDisc = (unit * toNum(l.orderDiscountPercent)) / 100;
    const lt = String(l.isLotteryTicket);
    const isLottery = !(lt === "0" || lt === "null" || lt === "");
    const discountAmount = isLottery ? 0 : (unit - itemDisc) * couponPct;
    const giftCardAmount = (unit - itemDisc) * giftCardPct;
    const loyaltyReduce = unit * loyPct;
    const priceWithDGL = unit - discountAmount - loyaltyReduce - itemDisc;
    const priceWithTax = (l.taxList || []).reduce((a, t) => a + roundHalfUp(((priceWithDGL * toNum(t.percent)) / 100) * count), 0);
    return priceWithDGL * count + priceWithTax - giftCardAmount;
  };
  const earnBase = cart.reduce((s, l) => (!l.canEarnPoint || isPayout(l) ? s : s + itemEarnBase(l, l.canUsePoint ? loyaltyPct : 0)), 0);
  let base = Math.min(roundHalfUp(earnBase), toNum(grandTotal)); // capped at grand total (Android)
  // Android getLoyaltyJson: a surcharge store earning on a NON-credit tender earns on the cash price
  // (dual: min(toBase(basis), getBaseGrandTotal)).
  if (typeof convertBase === "function" && base > 0) base = convertBase(base);
  const points = Math.max(roundHalfUp(rate * base), 0);
  return { points, amount: roundHalfUp(points * toNum(dollarPerPoint)) };
}

export function formatOrderDateTime(input) {
  if (typeof input === "string" && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(input)) return input;
  let d = input instanceof Date ? input : input ? new Date(input) : new Date();
  if (isNaN(d.getTime())) d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

export function buildSavePayload({
  cart = [],
  recalc = {},
  taxResult = {},
  couponTotal = 0,
  customTotal = 0,
  couponCode = "",
  couponId = "",
  isOutdoor = false,
  isEighteenPlus = false,
  cashPaid = 0,
  orderId,
  // Order-level reductions (paid down the grand total): gift card / loyalty / store credit.
  reductions = {},
  // Payment tenders paying the (reduced) balance: [{ payType:"cash"|"credit"|"debit"|"foodebt"|"cashebt",
  //   amount, tip, paymentId, cardType, last4, paxDetails, paymentResult, cvvResult }]. 1 = one-shot, 2+ = split.
  tenders = null,
  // Legacy single-tender convenience (still used by tests): { mode:"card", paymentId, cardType, ... }.
  payment = null,
  ctx = {},
}) {
  const grandTotal = toNum(recalc.grandTotal); // net of reductions — what the tenders pay

  let bogoDisc = 0;
  let mixMatchDisc = 0;
  for (const l of cart) {
    const catalog = catalogOf(l);
    const count = toNum(l.orderCount);
    if (l.isBoGoProduct) bogoDisc += (catalog - toNum(l.orderUnitPrice)) * count;
    if (l.isBulkPriceApplied) mixMatchDisc += (catalog - toNum(l.orderUnitNewPrice)) * count;
  }

  const taxByName = (taxResult.taxByName instanceof Map ? taxResult.taxByName : new Map());
  const defaultTaxPercent = taxByName.get("DefaultTax") ? S(taxByName.get("DefaultTax").percent) : "0.00";

  // ── Reductions (gift card / loyalty / store credit) — server debits real balances from these ──
  const r = reductions || {};
  const giftCards = Array.isArray(r.giftCards) ? r.giftCards : [];
  const giftCardTotal = giftCards.reduce((s, g) => s + toNum(g.amountUsed), 0);
  const loyaltyAmt = toNum(r.loyaltyAmount);
  const loyaltyPts = toNum(r.loyaltyPointsSpent);
  const scAmt = toNum(r.storeCreditAmount);

  // Top-level `discount` — Android = coupon+custom + BOGO + Mix&Match + getSumOfLpSc()
  // (loyalty-spent + store-credit-spent + gift-card-amount). The web previously omitted LP/SC/GC.
  const discount = roundHalfUp(
    toNum(couponTotal) + toNum(customTotal) + bogoDisc + mixMatchDisc + loyaltyAmt + scAmt + giftCardTotal
  );

  // ── Payment tenders → one-shot vs split ──
  let tenderList = Array.isArray(tenders) ? tenders.filter(Boolean) : null;
  if (!tenderList || tenderList.length === 0) {
    // Legacy path: a single card `payment`, or plain cash.
    const pay = payment && payment.mode === "card" ? payment : null;
    tenderList = pay
      ? [{ payType: "card", amount: grandTotal, paymentId: pay.paymentId, cardType: pay.cardType, paxDetails: pay.paxDetails, cvvResult: pay.cvvResult, paymentResult: pay.paymentResult, tip: pay.tip }]
      : [{ payType: "cash", amount: grandTotal }];
  }
  const isSplit = tenderList.length > 1;
  const isCash = (t) => String(t.payType).toLowerCase() === "cash";
  const cardTenders = tenderList.filter((t) => !isCash(t));
  const rep = cardTenders.length ? cardTenders[cardTenders.length - 1] : null; // top-level card fields
  const single = tenderList.length === 1 ? tenderList[0] : null;

  const tipTotal = tenderList.reduce((s, t) => s + toNum(t.tip), 0);
  // QV-4241: in dual price the tenders pay the CASH (base) total; the card premium rides in ncaAmount.
  const dualCfg = ctx.dualPrice || null;
  const dualT = isDualPrice(dualCfg) && recalc.dual && recalc.dual.dual ? recalc.dual : null;
  const owedTotal = dualT ? toNum(dualT.baseGrandTotal) : grandTotal;
  // Android getChangeDueAmount: one-shot cash → tendered − total; a split whose FINAL leg is cash →
  // tendered − that leg (the web booked min(tendered, due) on the leg).
  const lastTender = tenderList[tenderList.length - 1];
  const changeDue =
    single && isCash(single)
      ? Math.max(roundHalfUp(toNum(cashPaid) - owedTotal), 0)
      : isSplit && lastTender && isCash(lastTender) && toNum(cashPaid) > 0
      ? Math.max(roundHalfUp(toNum(cashPaid) - toNum(lastTender.amount)), 0)
      : 0;

  const topPaymentId = isSplit
    ? (rep ? S(rep.paymentId) : "Cash")
    : single && isCash(single)
      ? "Cash"
      : S((single || rep || {}).paymentId);
  const topCard = rep || (single && !isCash(single) ? single : null);

  // ── NCA/surcharge + debit cashback (per-tender, from register settings; 0 when disabled) ──
  const totalNca = roundHalfUp(tenderList.reduce((s, t) => s + toNum(t.ncaAmount), 0));
  const totalCashbackAmt = roundHalfUp(tenderList.reduce((s, t) => s + toNum(t.cashbackAmount), 0));
  const totalCashbackFee = roundHalfUp(tenderList.reduce((s, t) => s + toNum(t.cashbackFee), 0));
  const ncaPct = rep && rep.ncaPercent != null ? S(rep.ncaPercent) : "0.00";
  // `amt` = the full order value. Android re-adds store credit (+ lottery/scratch) back on top of the
  // reduced grand total ("Store Credit need to include in amt field") — loyalty/gift-card stay subtracted.
  // grandTotal is already net of SC, so add scAmt back. NCA + cashback (+fee) are collected on top.
  const payoutTotals = lotteryPayoutTotals(cart);
  const lotteryScratch = recalc.lotteryScratcher != null ? toNum(recalc.lotteryScratcher) : payoutTotals.lottery + payoutTotals.scratch;
  // Dual price (SaveOrderViewModel): cash/debit/EBT = base total; credit = card total = base + its NCA;
  // split = base + Σ leg NCA — i.e. always base + Σ tender NCA, + SC + payouts + cash-back.
  const chargedAmt = roundHalfUp(owedTotal + scAmt + lotteryScratch + totalNca + totalCashbackAmt + totalCashbackFee);

  // ── Loyalty EARN (points accrued) — port of getLoyaltyJson totalEarnPoint/totalEarnAmount ──
  const loy = ctx.loyalty || {};
  let lpEarnPts = 0;
  let lpEarnAmt = 0;
  // Android earns points ONLY when a customer is attached (getLoyaltyJson gates on customerModel != null).
  // The earn RATE = points_per_dollar + active bonus-point promotions (getTotalLoyaltyPoints, wired via
  // ctx.loyalty.bonusPointsPerDollar). The earn BASE is getAmountForEarnPointOnly: over the lines whose
  // category allows earning, each line's price reduced proportionally by the order-level coupon %, gift
  // card %, and (for use-point lines) loyalty %, plus that reduced line's tax, minus its gift-card share —
  // a byte-faithful port of getItemPriceWithDiscountTax. Capped at the grand total.
  const rate = toNum(loy.pointsPerDollar) + toNum(loy.bonusPointsPerDollar);
  if (loy.enabled && rate > 0 && ctx.customerId) {
    const earn = computeLoyaltyEarn({
      cart,
      rate,
      dollarPerPoint: toNum(loy.dollarPerPoint),
      couponDiscountValue: toNum(couponTotal) + toNum(customTotal),
      giftCardTotal,
      loyaltyAmt,
      grandTotal,
      convertBase:
        dualT && dualTenderKind(tenderList) === "cash"
          ? (b) => Math.min(toBasePrice(dualCfg, b), toNum(dualT.baseGrandTotal))
          : dualT && dualTenderKind(tenderList) === "split"
          ? (b) => {
              // Android blendedSplitEarnBasis + blendedPaidCap (totalSplitChargedAmount)
              const exempt = scAmt + lotteryScratch;
              const K = 1 + (1 - splitCashBaseFraction(tenderList, exempt)) * (toNum(dualCfg.pct) / 100);
              const paid = tenderList.reduce((a, t) => a + toNum(t.amount) + toNum(t.ncaAmount), 0);
              return Math.min(roundHalfUp(toBasePrice(dualCfg, b) * K), roundHalfUp(paid));
            }
          : null,
    });
    lpEarnPts = earn.points;
    lpEarnAmt = earn.amount;
  }

  const id = orderId || generateOrderId();
  const now = formatOrderDateTime(ctx.orderDateTime); // bare local "yyyy-MM-dd HH:mm:ss" (server strtotime()s it)

  // Build item_ses once, then derive top-level tax fields from it EXACTLY like Android
  // jsonDefaultTax()/jsonOtherTax() (which sum the per-item default_tax_total / other_tax_total).
  // `subtotal` field — Android getSubTotals() (OCV:179-208): per NON-lottery/scratcher line, use the
  // manual OVERRIDE price for a plain override (not BOGO, not bulk) else the catalog price, then
  // subtract the item-level discount ($ ×count, or % truncated per-unit). BOGO/bulk keep catalog (gross).
  const subtotalField = roundHalfUp(
    cart.reduce((s, l) => {
      if (l.isLotteryPayout || l.isScratcherPayout) return s;
      const count = toNum(l.orderCount);
      const isOverride = toNum(l.orderUnitNewPrice) > 0 && toNum(l.bogoIdentifier) === 0 && !l.isBulkPriceApplied;
      const unit = isOverride ? toNum(l.orderUnitNewPrice) : catalogOf(l);
      const pct = toNum(l.orderDiscountPercent);
      const itemDisc = toNum(l.orderDiscount) > 0 ? toNum(l.orderDiscount) * count : pct > 0 ? truncate2((unit * pct) / 100) : 0;
      return s + unit * count - itemDisc;
    }, 0)
  );
  const items = buildItemSession(cart, taxResult, ctx);
  const dualKind = dualTenderKind(tenderList);
  const payloadLines = cart.filter((l) => !(l.isSplitPaymentDiscount || l.isSplitPayment || l.isSplitRefund));
  const dualItems = dualT
    ? applyDualItemSession(items, payloadLines, {
        cfg: dualCfg,
        dual: dualT,
        kind: dualKind,
        loyalty: loyaltyAmt,
        couponDiscountValue: toNum(couponTotal) + toNum(customTotal),
        mSubTotal: toNum(recalc.subtotal),
        taxRateInAmount: toNum(recalc.taxRateInAmount != null ? recalc.taxRateInAmount : taxResult.taxRateInAmount),
        perLineBaseDefaultTax: taxResult.perLineBaseDefaultTax,
      })
    : { cashPayDiscount: 0 };
  // Phase C — dual SPLIT (no redeemed gift card): Android isSplitBlendPayload item/tax blend.
  const splitRes =
    dualT && dualKind === "split" && giftCardTotal === 0
      ? applyDualSplitBlend(items, payloadLines, {
          cfg: dualCfg,
          dual: dualT,
          tenders: tenderList,
          storeCredit: scAmt,
          lotteryScratcher: lotteryScratch,
          couponDiscountValue: toNum(couponTotal) + toNum(customTotal),
          taxRateInAmount: toNum(recalc.taxRateInAmount != null ? recalc.taxRateInAmount : taxResult.taxRateInAmount),
          subtotalField,
          bogoTotal: bogoDisc,
          mixMatchTotal: mixMatchDisc,
        })
      : null;
  if (splitRes) dualItems.cashPayDiscount = splitRes.cashPayDiscount;
  const taxDefault = roundHalfUp(items.reduce((s, it) => s + toNum(it.default_tax_total), 0)); // → `tax`
  const taxOther = roundHalfUp(items.reduce((s, it) => s + toNum(it.other_tax_total), 0));   // → `other_taxes`
  const taxTotal = roundHalfUp(taxDefault + taxOther);
  // actual_taxes — Android getActualTaxableAmount: with loyalty applied, scale total tax by the loyalty
  // fraction of the subtotal (totalTax × (mSubTotal − loyalty)/mSubTotal); no loyalty → the full tax.
  const mSub = toNum(recalc.subtotal);
  const actualTaxes = loyaltyAmt > 0 && mSub > 0 ? roundHalfUp((taxTotal * (mSub - loyaltyAmt)) / mSub) : taxTotal;
  // Order-level other-tax descriptors: {name: total amount} / {name: percent} (getOtherTaxesDesc/RateDesc).
  const otherDescObj = {};
  const otherRateObj = {};
  for (const it of items) {
    if (!it.other_tax_desc) continue;
    try {
      const d = JSON.parse(it.other_tax_desc);
      const r = JSON.parse(it.other_taxes_rate_desc || "{}");
      for (const k of Object.keys(d)) otherDescObj[k] = roundHalfUp(toNum(otherDescObj[k]) + toNum(d[k]));
      for (const k of Object.keys(r)) otherRateObj[k] = r[k];
    } catch {
      /* ignore malformed */
    }
  }

  // Full loyalty JSON carried in `coupon_code` (Android getLoyaltyJson — exact keys).
  const couponName = couponCode ? String(couponCode) : isOutdoor ? "OTD" : "Discount";
  const surchargeMode = normalizeSurchargeMode(dualCfg && dualCfg.mode);
  const dualF = dualT
    ? dualOrderFields({
        cfg: dualCfg,
        dual: dualT,
        kind: dualKind,
        lines: payloadLines,
        subtotalField,
        priceDiscount: toNum(couponTotal) + toNum(customTotal) + bogoDisc + mixMatchDisc,
        lpScGc: loyaltyAmt + scAmt + giftCardTotal,
        mGrandTotal: grandTotal,
        totalNca,
        cashPayDiscount: dualItems.cashPayDiscount,
        split: splitRes,
      })
    : null;
  const jsonScale = dualF && dualF.scaleCouponJson;
  const M = surchargeMultiplier(dualCfg);
  const loyaltyJson = {
    coupon_code: couponName,
    coupon_code_amt: splitRes
      ? roundHalfUp(splitRes.blendOrderDiscount * splitRes.carried)
      : jsonScale
      ? toBasePrice(dualCfg, toNum(couponTotal) + toNum(customTotal))
      : roundHalfUp(toNum(couponTotal) + toNum(customTotal)),
    bogo_discount: roundHalfUp(splitRes ? (bogoDisc / M) * splitRes.K : jsonScale ? bogoDisc / M : bogoDisc),
    mix_match_discount: roundHalfUp(splitRes ? (mixMatchDisc / M) * splitRes.K : jsonScale ? mixMatchDisc / M : mixMatchDisc),
    loyalty_point_earned: lpEarnPts || 0,
    loyalty_point_amt_earned: lpEarnAmt || 0,
    loyalty_point_amt_spent: loyaltyAmt || 0,
    loyalty_point_spent: loyaltyPts || 0,
    store_credit_amt_spent: scAmt || 0,
    gift_card_number: giftCards[0] ? S(giftCards[0].number) : "0.00",
    gift_card_amount: giftCards[0] ? roundHalfUp(toNum(giftCards[0].amountUsed)) : "0.00",
    gift_card_balance: giftCards[0] && giftCards[0].balance != null ? roundHalfUp(toNum(giftCards[0].balance)) : "0.00",
    // Android getLoyaltyJson: the order carries its OWN surcharge mode (summaries/refunds read it from
    // here, never from the live store setting) + the label only when a surcharge mode is on + the NCA.
    surcharge_label: surchargeMode === "0" ? "" : "Non-Cash Adjustment",
    is_surcharge: surchargeMode,
    nca: dualF ? roundHalfUp(dualF.nca) : surchargeMode === "1" ? totalNca : 0,
    employee_name: S(ctx.employeeName),
    // Android getLoyaltyJson lottery block: payout totals + the order-pay / cash-pay split (how much of
    // each payout was netted against the sale vs handed over as cash). mGrandTotal = recalc.grandTotal
    // (already net of the payouts; negative when the customer is owed cash).
    ...lotteryPayoutJson({ subtotal: toNum(recalc.subtotal), grandTotal: grandTotal, lottery: payoutTotals.lottery, scratch: payoutTotals.scratch }),
  };

  return {
    // identity / status
    order_id: id,
    merchant_id: S(ctx.merchantId),
    employee_id: S(ctx.employeeId),
    admin_id: S(ctx.adminId || ctx.merchantId),
    group_id: S(ctx.groupId),
    customer_id: S(ctx.customerId),
    customer_app_id: "",
    order_status: "paid",
    Paymentstts: "1",
    payment_id: topPaymentId,
    is_split_payment: isSplit ? "1" : "0",
    is_online: "0",

    // card result fields (from the representative card tender; "" when none)
    card_type: topCard ? S(topCard.cardType) : "",
    payment_result: topCard ? S(topCard.paymentResult || "Approved") : "",
    cvvResult: topCard ? S(topCard.cvvResult) : "",
    failResult: "",
    pax_details: topCard ? S(topCard.paxDetails) : "",

    // cart + money
    item_ses: JSON.stringify(items),
    subtotal: money(dualF ? dualF.subtotal : subtotalField), // Android getPayloadSubTotal (getSubTotals off dual)
    discount: money(dualF ? dualF.discount : discount),
    tax: money(taxDefault), // default tax ONLY (Android jsonDefaultTax); other taxes go to other_taxes
    tax_rate: defaultTaxPercent,
    actual_taxes: money(actualTaxes), // ALL taxes, loyalty-adjusted (Android getActualTaxableAmount)
    other_taxes: money(taxOther), // other tax total ONLY (Android jsonOtherTax) — numeric
    other_taxes_desc: Object.keys(otherDescObj).length ? JSON.stringify(otherDescObj) : "",
    other_taxes_rate_desc: Object.keys(otherRateObj).length ? JSON.stringify(otherRateObj) : "",
    amt: money(chargedAmt),
    tip: tipTotal > 0 ? money(tipTotal) : "",
    change_due: money(changeDue),
    cash_discounting: dualF ? money(dualF.cashDiscounting) : totalNca > 0 ? money(totalNca) : "0.00",
    cash_discounting_percentage: dualF ? dualNcaPercent(dualKind, single, dualCfg) : ncaPct,
    cash_pay_discount: money(dualF ? dualF.cashPayDiscount : 0),
    cash_back_amt: totalCashbackAmt > 0 ? money(totalCashbackAmt) : "0.00",
    cash_back_fee: totalCashbackFee > 0 ? money(totalCashbackFee) : "0.00",

    // discounts / flags — coupon_code carries the loyalty JSON (Android); the coupon is keyed by coupon_id
    coupon_code: JSON.stringify(loyaltyJson),
    coupon_id: S(couponId),
    is_outdoor: isOutdoor ? "1" : "0",
    is_eighteen_plus: isEighteenPlus ? "1" : "0",
    id_card_no: S(ctx.idNumber), // server field is id_card_no (NOT id_number — that was silently dropped)
    id_card_type: S(ctx.idCardType),
    dob: S(ctx.dob),
    id_card_ex_date: S(ctx.idCardExDate),
    m_status: "4", // paid order status flag (Android sets "4" post-payment)
    lottery_scratch: lotteryScratcherStatus(payoutTotals.lottery, payoutTotals.scratch, grandTotal), // Android lotteryScratcherStatus
    is_split_line_item: "0",
    batch_usage: S(ctx.batchUsage),
    adv_id: S(ctx.advId),
    shift_setting: S(ctx.shiftSetting),
    future_order: "",
    is_future: "0",
    del_fee: "0.00",
    con_fee: "0.00",

    // reductions (server debits balances from these) — is_X: "0" none / "1" partial-or-combined / "2" alone-covers-all
    is_loyality: tenderFlag(loyaltyAmt, grandTotal, giftCardTotal, scAmt),
    is_store_credit: tenderFlag(scAmt, grandTotal, giftCardTotal, loyaltyAmt),
    is_gift_card: tenderFlag(giftCardTotal, grandTotal, loyaltyAmt, scAmt),
    giftcard_json: buildGiftcardJson(giftCards, cart, id), // issued "Gift Card(NUM)" lines (credit) + redeems (debit)
    lp_spend_amount: loyaltyAmt > 0 ? money(loyaltyAmt) : "",
    lp_spend_points: loyaltyAmt > 0 ? S(loyaltyPts) : "",
    lp_earn_amount: lpEarnAmt > 0 ? money(lpEarnAmt) : "",
    lp_earn_points: lpEarnPts > 0 ? S(lpEarnPts) : "",
    sc_spend_amount: scAmt > 0 ? money(scAmt) : "",

    // split tenders (only when 2+ payment tenders)
    splitpay_json: isSplit ? buildSplitpayJson(tenderList, owedTotal, now) : "",

    // order meta
    order_date_time: now,
    merchant_time: now, // Android sends merchant_time == order_date_time (device-local)
    order_method: S(ctx.orderMethod || "pickup"),
    order_type: S(ctx.orderMethod || "pickup"),
    enable_order_number: S(ctx.enableOrderNumber || "0"),
    order_number: "0",

    // mKonnekt (Dezi) sale-time capture for the server-side reporting sweep — optional, "" when Dezi
    // is off, no lookup was applied, or the applied quote was not asked for this cart + customer phone
    // (see engine/dezi deziSaveOrderFields).
    mkonnekt_discount_response: S(ctx.mkonnektDiscountResponse),
    mkonnekt_mobile_number: S(ctx.mkonnektMobileNumber),
    mkonnekt_rcn: S(ctx.mkonnektRcn),
  };
}

// SaveOrderViewModel.setPayData cash_discounting_percentage under dual price: credit → the credit %
// setting; debit → the DEBIT % setting (sent even in dual — Android quirk kept); cash/EBT/split → "0.00".
function dualNcaPercent(kind, single, cfg) {
  if (kind === "split") return "0.00";
  const pt = String((single && single.payType) || "").toLowerCase();
  if (kind === "credit") return toNum(cfg && cfg.pct).toFixed(2);
  if (pt === "debit") return toNum(cfg && cfg.debitSetting).toFixed(2);
  return "0.00";
}
